import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

// Traced from the client's white-background reference (1600×800 units).
// Low crossing ≈ (640, 660), high crossing ≈ (1280, 400).
const MINERAL = "#6C91B1";
const PALE = "#E3EDF5";
const GREY = "rgba(95,107,115,0.28)";
const TEAL = "#23716B";

const TEAL_PATH = "M-20 735 C 250 705 450 650 640 662 C 800 672 900 590 1000 570 C 1110 548 1180 470 1280 400 C 1380 330 1460 220 1640 140";
const MAIN_PATH = "M-20 650 C 220 610 420 640 640 656 C 860 672 1050 560 1280 400 C 1420 300 1500 180 1640 60";

const LINES = [
  { d: MAIN_PATH, c: MINERAL, o: 0.75 },
  { d: "M-20 690 C 200 640 450 640 640 660 C 820 680 1000 600 1120 520 C 1200 470 1240 430 1280 400 C 1360 330 1420 150 1520 -20", c: MINERAL, o: 0.65 },
  { d: "M300 820 C 500 700 600 680 640 656 C 760 590 1100 520 1280 400 C 1380 330 1480 120 1600 40", c: MINERAL, o: 0.55 },
  { d: "M380 820 C 560 720 860 640 1100 540 C 1180 500 1240 440 1280 400 C 1330 350 1390 90 1460 -20", c: MINERAL, o: 0.6 },
  { d: "M-20 700 C 150 660 300 650 450 670 C 600 690 700 720 900 700 C 1100 680 1300 600 1640 520", c: MINERAL, o: 0.5 },
  { d: "M-20 720 C 200 690 350 720 500 740 C 700 765 1000 720 1640 640", c: MINERAL, o: 0.4 },
  { d: "M-20 760 C 300 720 520 680 700 610 C 880 540 1050 440 1180 432 C 1300 434 1400 460 1640 480", c: MINERAL, o: 0.7 },
  { d: "M1640 330 C 1400 340 1250 360 1100 420 C 900 500 700 620 500 690 C 300 760 100 790 -20 800", c: MINERAL, o: 0.45 },
  { d: "M1640 380 C 1450 390 1300 410 1180 460 C 1000 540 850 600 640 656 C 450 706 200 730 -20 760", c: MINERAL, o: 0.55 },
  { d: "M560 820 C 800 760 950 640 1050 560 C 1150 480 1250 300 1360 160 C 1420 80 1480 30 1640 0", c: MINERAL, o: 0.4 },
  { d: "M700 820 C 900 760 1000 700 1060 640 C 1200 540 1300 420 1400 300 C 1480 200 1550 140 1640 110", c: MINERAL, o: 0.5 },
  { d: "M400 820 C 700 790 1100 700 1640 600", c: MINERAL, o: 0.35 },
  { d: "M-20 670 C 300 680 480 720 700 720 C 950 720 1200 640 1640 560", c: MINERAL, o: 0.35 },
  { d: "M-20 615 C 250 605 450 615 640 650 C 800 690 1000 700 1640 690", c: GREY, o: 1 },
  { d: "M820 820 C 1000 740 1150 620 1200 560 C 1300 480 1400 380 1640 300", c: GREY, o: 1 },
  { d: "M560 820 C 760 740 1000 700 1640 760", c: GREY, o: 1 },
];

const RIBBONS = [
  "M240 820 C 600 700 900 600 1100 480 C 1250 380 1380 150 1480 -20 L1600 -20 C 1450 160 1320 400 1180 500 C 1000 620 750 700 420 820 Z",
  "M-20 640 C 250 610 450 640 640 662 C 820 682 1000 640 1640 500 L1640 560 C 1000 700 820 710 640 700 C 450 690 250 660 -20 680 Z",
  "M1100 540 C 1250 420 1350 300 1450 160 C 1500 90 1560 50 1640 30 L1640 90 C 1600 100 1550 130 1500 200 C 1400 330 1300 450 1100 540 Z",
];

const Traveller = ({ d, color, width, duration, delay, repeatDelay }) => (
  <motion.path
    d={d}
    pathLength={1}
    fill="none"
    stroke={color}
    strokeWidth={width}
    strokeLinecap="round"
    strokeDasharray="0.06 1"
    initial={{ strokeDashoffset: 0.06, opacity: 0 }}
    animate={{ strokeDashoffset: [0.06, -0.94], opacity: [0, 1, 1, 0] }}
    transition={{ duration, delay, repeatDelay, repeat: Infinity, ease: "easeInOut", times: [0, 0.12, 0.85, 1] }}
  />
);

// band=true shows only the lower waves and the low crossing (tablet / phone).
export const HeroFlow = ({ travellers = 2, band = false, className = "w-full h-full" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { amount: 0.1 });
  const animate = !reduce && inView;

  return (
    <svg
      ref={ref}
      viewBox={band ? "0 560 1600 260" : "0 0 1600 800"}
      preserveAspectRatio={band ? "xMidYMax slice" : "xMidYMin meet"}
      className={`overflow-visible ${className}`}
      aria-hidden="true"
      focusable="false"
      data-testid="hero-flow-graphic"
    >
      {RIBBONS.map((r) => (
        <path key={r.slice(0, 12)} d={r} fill={PALE} opacity="0.7" />
      ))}
      {LINES.map((l) => (
        <path key={l.d.slice(0, 16)} d={l.d} fill="none" stroke={l.c} strokeOpacity={l.o} strokeWidth="1.3" />
      ))}
      <path d={TEAL_PATH} fill="none" stroke={TEAL} strokeOpacity="0.9" strokeWidth="1.5" />
      {animate && (
        <>
          <Traveller d={TEAL_PATH} color={TEAL} width={2.2} duration={10} delay={1.5} repeatDelay={5} />
          {travellers > 1 && <Traveller d={MAIN_PATH} color={MINERAL} width={2} duration={12} delay={7} repeatDelay={6} />}
        </>
      )}
    </svg>
  );
};
